/**
 * Client-side GA4 event wrapper.
 *
 * All non-conversion marketing events should fire through `trackEvent` rather
 * than inlining `window.gtag(...)` — this gives us one place to:
 *
 *   - guard for SSR and missing gtag (Consent Mode denied, blocker active,
 *     hydration-race, etc.) so callers don't need to repeat the checks
 *   - stamp a consistent `event_category` / `event_label` default shape
 *   - evolve the shim later (batching, server-side mirror, per-env toggles)
 *     without touching every call site
 *
 * Consent-mode note. The underlying `gtag` function is loaded by
 * `components/analytics/google-analytics.tsx` with `strategy="afterInteractive"`,
 * which means on first-paint of a page `window.gtag` may not exist yet.
 * We intentionally no-op in that window — events that fire before gtag
 * loads are lost rather than queued, because a queued event can double-fire
 * if consent flips after load. The inline head script in `app/layout.tsx`
 * registers `window.dataLayer` early, so page_view firing is handled by GA4
 * itself; the events we care about here all fire on user interaction and
 * happen well after hydration.
 *
 * Coverage. Events added in gh#10:
 *   - `first_doc_generated`   — components/app/first-doc-celebration.tsx
 *   - `doc_downloaded`        — components/documents/document-detail-view.tsx
 *                             - components/documents/version-sidebar.tsx
 *   - `upgrade_clicked`       — app/(marketing)/pricing/pricing-cta.tsx
 *                             - components/app/upgrade-nudge.tsx
 *   - `purchase`              — app/(app)/billing/success/page.tsx
 *                               (server-side mirror in app/api/stripe/webhook/route.ts
 *                               via lib/analytics-server.ts)
 *
 * `returned_day_2` is intentionally deferred to a follow-up — needs a
 * signup-time cookie that isn't currently set at onboarding, and is a
 * standalone layout-level tracker better shipped on its own.
 */

import { readAnalyticsConsent } from "@/components/analytics/browser-attribution";
import { sendGoogleTagEventIfReady } from "@/lib/analytics/google-tag-readiness";
import { GA_ID } from "@/lib/analytics/google-ids";

type EventParamValue = string | number | boolean | null | undefined;

export type EventParams = Record<string, EventParamValue>;

/**
 * Fire a GA4 custom event. Returns true only after the event has been queued
 * behind the granted-consent and GA4 config commands. Safe to call
 * unconditionally from any client component.
 */
export function trackEvent(name: string, params: EventParams = {}): boolean {
  if (typeof window === "undefined") return false;
  if (readAnalyticsConsent() !== "accepted") return false;

  // Strip undefined values — GA4 treats them as the literal string "undefined"
  // which pollutes Exploration dimensions. null is allowed and meaningful.
  const cleaned: Record<string, Exclude<EventParamValue, undefined>> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined) cleaned[k] = v;
  }

  return sendGoogleTagEventIfReady("analytics", name, {
    ...cleaned,
    send_to: GA_ID,
  });
}
